// Kept temporarily for legacy helper signatures while all runtime callers use Worker APIs.
type LegacyDataClient = any;

export const SYSTEM_ADMIN_PAGE_SIZE = 50;
export const LOW_OC_THRESHOLD = 100;
export const USER_PASSWORD_RESET_CONFIRMATION = 'RESET PASSWORD';
export const USER_PASSWORD_RESET_MIN_LENGTH = 12;
export const CREATE_ADMIN_USER_PASSWORD_MIN_LENGTH = 12;
const ADMIN_USERNAME_PATTERN = /^[a-zA-Z0-9._-]{3,64}$/;

export interface Cursor {
  createdAt: string;
  id: string;
}

export interface PaginatedResult<T> {
  rows: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface DashboardMetric {
  label: string;
  value: string;
  note: string;
}

export interface FranchiseRow {
  id: string;
  name: string;
  status: string;
  oc_balance: number;
  region: string | null;
  commission_rate: number | string | null;
  exchange_rate: number | string | null;
  created_at: string;
  updated_at: string;
}

export interface StoreRow {
  id: string;
  name: string;
  oc_balance: number;
  /** 매장 포인트 환율: 1,000포인트당 원 */
  krw_per_1000_points: number | string | null;
  franchise_id: string | null;
  created_at: string;
  updated_at: string;
  franchise?: { id: string; name: string } | null;
}

export interface UserRow {
  id: string;
  role: string;
  internal_login_id: string | null;
  nickname: string | null;
  username: string | null;
  phone_number: string | null;
  staff_phone_number: string | null;
  status: string;
  store_id: string | null;
  franchise_id: string | null;
  created_at: string;
  last_active_at: string | null;
  store?: { id: string; name: string } | null;
  franchise?: { id: string; name: string } | null;
}

export interface OcTransactionRow {
  id: string;
  store_id: string | null;
  franchise_id: string | null;
  transaction_type: string;
  amount: number;
  amount_oc: number | null;
  amount_krw: number | string | null;
  balance_after: number;
  description: string | null;
  created_at: string;
}

export interface OcRequestRow {
  id: string;
  franchise_id: string;
  franchise_name: string;
  store_id: string | null;
  store_name: string | null;
  request_type: 'issue' | 'adjust';
  amount: number;
  amount_krw: number | null;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  requested_by: string;
  requested_by_name: string | null;
  reviewed_by: string | null;
  review_reason: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OcRequestReviewInput {
  id: string;
  reviewReason: string;
}

export interface AuditLogRow {
  id: string;
  created_at: string;
  admin_id: string;
  action_type: string;
  target_type: string;
  target_id: string | null;
  store_id: string | null;
}

export interface SystemSettingRow {
  id: string;
  key: string;
  value: unknown;
  description: string | null;
  value_type: string;
  category: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface SystemDashboardReadModel {
  metrics: DashboardMetric[];
  lowBalanceFranchises: FranchiseRow[];
  recentOcTransactions: OcTransactionRow[];
  recentAuditLogs: AuditLogRow[];
}

export interface FranchiseFilters {
  search?: string;
  status?: string;
}

export interface StoreFilters {
  search?: string;
  lowOcOnly?: boolean;
}

export interface UserFilters {
  search?: string;
  role?: string;
  status?: string;
}

export interface OcFilters {
  transactionType?: string;
}

export type OcRequestStatusFilter = 'pending' | 'approved' | 'rejected' | 'cancelled' | '';

export interface AuditFilters {
  search?: string;
  targetType?: string;
}

export interface SettingFilters {
  category?: string;
}

export interface CreateFranchiseInput {
  commissionRate: number;
  exchangeRate: number;
  name: string;
  ocBalance: number;
  region: string;
}

export interface UpdateFranchiseInput {
  beforeValue: FranchiseRow;
  commissionRate: number;
  exchangeRate: number;
  id: string;
  name: string;
  region: string;
}

export interface SetFranchiseStatusInput {
  beforeValue: FranchiseRow;
  confirmationText: string;
  id: string;
  reason: string;
  status: 'active' | 'inactive';
}

export interface CreateStoreInput {
  krwPer1000Points: number;
  franchiseId: string;
  name: string;
  ocBalance: number;
}

export interface UpdateStoreInput {
  beforeValue: StoreRow;
  krwPer1000Points: number;
  franchiseId: string;
  id: string;
  name: string;
}

export interface SuspendUserInput {
  currentAdminId: string;
  duration: string;
  reason: string;
  userId: string;
}

export interface UnsuspendUserInput {
  currentAdminId: string;
  reason: string;
  userId: string;
}

export interface ResetUserPasswordInput {
  confirmPassword: string;
  confirmationText: string;
  currentAdminId: string;
  newPassword: string;
  reason: string;
  source?: string;
  userId: string;
}

export interface CreateAdminUserInput {
  confirmPassword: string;
  franchiseId: string;
  nickname: string;
  password: string;
  reason: string;
  role: 'staff_admin' | 'super_admin';
  source?: string;
  staffPhoneNumber: string;
  storeId: string;
  username: string;
}

export interface IssueOcCreditInput {
  amount: number;
  amountKrw: number;
  franchiseId: string;
  reason: string;
}

export interface AdjustOcCreditInput {
  amount: number;
  franchiseId: string;
  reason: string;
}

export interface OcMutationResult {
  amount: number;
  new_balance: number;
  transaction_id: string;
}

export interface UpdateSystemSettingInput {
  adminId: string;
  beforeValue: unknown;
  confirmationText: string;
  id: string;
  key: string;
  reason: string;
  valueText: string;
  valueType: string;
}

interface CountResponse {
  count: number | null;
  error: { message: string } | null;
}

interface DataResponse<T> {
  data: T | null;
  error: { message: string } | null;
}

interface ResetPasswordFunctionResponse {
  ok?: boolean;
  error?: string;
}

interface CreateAdminUserFunctionResponse {
  ok?: boolean;
  error?: string;
  user?: UserRow;
}

const FRANCHISE_SELECT = 'id, name, status, oc_balance, region, commission_rate, exchange_rate, created_at, updated_at';
const STORE_SELECT = 'id, name, oc_balance, krw_per_1000_points, franchise_id, created_at, updated_at, franchise:franchises(id, name)';
const USER_SELECT =
  'id, role, internal_login_id, nickname, username, phone_number, staff_phone_number, status, store_id, franchise_id, created_at, last_active_at, store:stores(id, name), franchise:franchises(id, name)';

export function encodeCursor(row: { created_at: string; id: string }): string {
  return encodeURIComponent(`${row.created_at}|${row.id}`);
}

export function decodeCursor(cursor: string): Cursor {
  const [createdAt, id] = decodeURIComponent(cursor).split('|');

  if (!createdAt || !id) {
    throw new Error('잘못된 페이지 커서입니다.');
  }

  return { createdAt, id };
}

export function formatInteger(value: number | null | undefined): string {
  return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 }).format(value ?? 0);
}

export function formatCurrency(value: number | string | null | undefined): string {
  const numericValue = typeof value === 'string' ? Number(value) : value;
  return `${formatInteger(Number.isFinite(numericValue) ? Number(numericValue) : 0)}원`;
}

export function formatOc(value: number | string | null | undefined): string {
  const numericValue = typeof value === 'string' ? Number(value) : value;
  return `${formatInteger(Number.isFinite(numericValue) ? Number(numericValue) : 0)} OC`;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat('ko-KR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

export function maskPhone(value: string | null | undefined): string {
  if (!value) {
    return '-';
  }

  const digits = value.replace(/\D/g, '');

  if (digits.length < 7) {
    return value;
  }

  return `${digits.slice(0, 3)}-****-${digits.slice(-4)}`;
}

export function shortId(value: string | null | undefined): string {
  if (!value) {
    return '-';
  }

  return value.length > 12 ? `${value.slice(0, 8)}...` : value;
}

export function getUserAccountId(row: Pick<UserRow, 'id' | 'internal_login_id' | 'phone_number' | 'username'>): string {
  return row.username ?? row.internal_login_id ?? row.phone_number ?? shortId(row.id);
}

function assertNoError<T extends { error: { message: string } | null }>(response: T, fallback: string): T {
  if (response.error) {
    throw new Error(response.error.message || fallback);
  }

  return response;
}

function countOrZero(response: CountResponse): number {
  return response.count ?? 0;
}

function rowsOrEmpty<T>(response: DataResponse<T[]>): T[] {
  return response.data ?? [];
}

async function callRpc<T>(
  client: LegacyDataClient,
  functionName: string,
  params: Record<string, unknown>,
  errorLabel: string,
  emptyLabel: string
): Promise<T> {
  const response = (await client.rpc(functionName, params)) as DataResponse<T>;

  assertNoError(response, errorLabel);

  if (!response.data) {
    throw new Error(emptyLabel);
  }

  return response.data;
}

function executeQuery<T>(query: unknown): Promise<T> {
  return query as Promise<T>;
}

function normalizeReason(reason: string): string {
  const normalized = reason.trim();

  if (normalized.length < 3) {
    throw new Error('작업 사유를 3자 이상 입력하세요.');
  }

  return normalized;
}

function roleRank(role: string | null | undefined): number {
  switch (role) {
    case 'platform_admin':
      return 3;
    case 'super_admin':
      return 2;
    case 'staff_admin':
      return 1;
    case 'customer':
      return 0;
    default:
      return -1;
  }
}

export async function readFunctionErrorMessage(error: unknown): Promise<string | null> {
  const context = (error as { context?: unknown })?.context;

  if (context instanceof Response) {
    const body = (await context
      .clone()
      .json()
      .catch(() => null)) as { error?: unknown } | null;

    if (typeof body?.error === 'string') {
      return body.error;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return null;
}

function normalizeRequiredText(value: string, label: string, minLength = 2, maxLength = 80): string {
  const normalized = value.trim();

  if (normalized.length < minLength) {
    throw new Error(`${label}은 ${minLength}자 이상 입력하세요.`);
  }

  if (normalized.length > maxLength) {
    throw new Error(`${label}은 ${maxLength}자 이하로 입력하세요.`);
  }

  return normalized;
}

function normalizeNullableText(value: string): string | null {
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeInteger(value: number, label: string): number {
  if (!Number.isInteger(value)) {
    throw new Error(`${label}은 정수로 입력하세요.`);
  }

  return value;
}

function normalizeNonNegativeInteger(value: number, label: string): number {
  const normalized = normalizeInteger(value, label);

  if (normalized < 0) {
    throw new Error(`${label}은 0 이상이어야 합니다.`);
  }

  return normalized;
}

function normalizePositiveNumber(value: number, label: string): number {
  if (!Number.isFinite(value)) {
    throw new Error(`${label}은 숫자로 입력하세요.`);
  }

  if (value <= 0) {
    throw new Error(`${label}은 0보다 커야 합니다.`);
  }

  return value;
}

function normalizeCommissionRate(value: number): number {
  if (!Number.isFinite(value)) {
    throw new Error('수수료율은 숫자로 입력하세요.');
  }

  if (value < 0 || value > 100) {
    throw new Error('수수료율은 0 이상 100 이하로 입력하세요.');
  }

  return value;
}

export function parseSystemSettingValue(valueText: string, valueType: string): unknown {
  const trimmed = valueText.trim();

  switch (valueType) {
    case 'number': {
      const value = Number(trimmed);
      if (!Number.isFinite(value)) {
        throw new Error('숫자 값을 입력하세요.');
      }
      return value;
    }
    case 'boolean':
      if (trimmed === 'true') {
        return true;
      }
      if (trimmed === 'false') {
        return false;
      }
      throw new Error('boolean 값은 true 또는 false로 입력하세요.');
    case 'string':
      if (valueText.length === 0) {
        throw new Error('문자열 값을 입력하세요.');
      }
      return valueText;
    case 'json':
      try {
        return JSON.parse(trimmed);
      } catch {
        throw new Error('유효한 JSON 값을 입력하세요.');
      }
    default:
      throw new Error(`지원하지 않는 설정 타입입니다: ${valueType}`);
  }
}

export function stringifySystemSettingValue(value: unknown, valueType: string): string {
  if (valueType === 'string') {
    return typeof value === 'string' ? value : String(value ?? '');
  }

  if (valueType === 'json') {
    return JSON.stringify(value, null, 2);
  }

  return String(value ?? '');
}

export function assertConfirmation(input: string, phrase: string): void {
  if (input.trim() !== phrase) {
    throw new Error(`확인 문구를 정확히 입력하세요: ${phrase}`);
  }
}

export function canRequestUserPasswordReset(
  currentAdminId: string | null | undefined,
  currentRole: string | null | undefined,
  target: Pick<UserRow, 'id' | 'role'>
): boolean {
  if (!currentAdminId || currentAdminId === target.id) {
    return false;
  }

  return roleRank(currentRole) > roleRank(target.role);
}

export function validateUserPasswordResetInput(input: ResetUserPasswordInput): string | null {
  if (input.userId === input.currentAdminId) {
    return '본인 계정은 여기서 재설정할 수 없습니다.';
  }

  if (input.newPassword.length < USER_PASSWORD_RESET_MIN_LENGTH) {
    return `임시 비밀번호는 ${USER_PASSWORD_RESET_MIN_LENGTH}자 이상이어야 합니다.`;
  }

  if (input.newPassword.trim() !== input.newPassword) {
    return '임시 비밀번호 앞뒤 공백은 사용할 수 없습니다.';
  }

  if (input.newPassword !== input.confirmPassword) {
    return '임시 비밀번호 확인이 일치하지 않습니다.';
  }

  if (input.reason.trim().length < 3) {
    return '재설정 사유를 3자 이상 입력하세요.';
  }

  if (input.confirmationText.trim() !== USER_PASSWORD_RESET_CONFIRMATION) {
    return `확인 문구를 정확히 입력하세요: ${USER_PASSWORD_RESET_CONFIRMATION}`;
  }

  return null;
}

export function validateCreateAdminUserInput(input: CreateAdminUserInput): string | null {
  const username = input.username.trim();

  if (input.role !== 'staff_admin' && input.role !== 'super_admin') {
    return '생성 가능한 관리자 역할을 선택하세요.';
  }

  if (!ADMIN_USERNAME_PATTERN.test(username)) {
    return '관리자 ID는 영문, 숫자, 점, 밑줄, 하이픈 3-64자만 사용할 수 있습니다.';
  }

  if (input.nickname.trim().length < 2) {
    return '이름 또는 표시명을 2자 이상 입력하세요.';
  }

  if (input.password.length < CREATE_ADMIN_USER_PASSWORD_MIN_LENGTH) {
    return `임시 비밀번호는 ${CREATE_ADMIN_USER_PASSWORD_MIN_LENGTH}자 이상이어야 합니다.`;
  }

  if (input.password.trim() !== input.password) {
    return '임시 비밀번호 앞뒤 공백은 사용할 수 없습니다.';
  }

  if (input.password !== input.confirmPassword) {
    return '임시 비밀번호 확인이 일치하지 않습니다.';
  }

  if (input.role === 'super_admin' && !input.franchiseId) {
    return '프랜차이즈 관리자는 프랜차이즈를 선택해야 합니다.';
  }

  if (input.role === 'staff_admin' && !input.storeId) {
    return '매장 관리자는 매장을 선택해야 합니다.';
  }

  if (input.reason.trim().length < 3) {
    return '계정 생성 사유를 3자 이상 입력하세요.';
  }

  return null;
}

function buildPage<T extends { created_at: string; id: string }>(rows: T[], limit: number): PaginatedResult<T> {
  const hasMore = rows.length > limit;
  const pageRows = hasMore ? rows.slice(0, limit) : rows;
  const lastRow = pageRows[pageRows.length - 1];

  return {
    rows: pageRows,
    hasMore,
    nextCursor: hasMore && lastRow ? encodeCursor(lastRow) : null,
  };
}

function startOfRecentDay(): string {
  return new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
}

export async function fetchSystemDashboardReadModel(client: LegacyDataClient): Promise<SystemDashboardReadModel> {
  const since = startOfRecentDay();

  const [
    activeFranchises,
    stores,
    users,
    franchiseBalances,
    dailyActiveUsers,
    dailyTransactions,
    lowBalanceFranchises,
    recentOcTransactions,
    recentAuditLogs,
  ] = await Promise.all([
    executeQuery<CountResponse>(client.from('franchises').select('id', { count: 'exact', head: true }).eq('status', 'active')),
    executeQuery<CountResponse>(client.from('stores').select('id', { count: 'exact', head: true })),
    executeQuery<CountResponse>(client.from('users').select('id', { count: 'exact', head: true }).neq('role', 'platform_admin')),
    executeQuery<DataResponse<FranchiseRow[]>>(
      client.from('franchises').select('id, name, status, oc_balance, region, commission_rate, exchange_rate, created_at, updated_at')
    ),
    executeQuery<CountResponse>(client.from('users').select('id', { count: 'exact', head: true }).gte('last_active_at', since)),
    executeQuery<DataResponse<Array<Pick<OcTransactionRow, 'id' | 'amount_krw' | 'amount_oc' | 'transaction_type' | 'created_at'>>> & CountResponse>(
      client.from('oc_transactions').select('id, amount_krw, amount_oc, transaction_type, created_at', { count: 'exact' }).gte('created_at', since).limit(1000)
    ),
    executeQuery<DataResponse<FranchiseRow[]>>(
      client
        .from('franchises')
        .select('id, name, status, oc_balance, region, commission_rate, exchange_rate, created_at, updated_at')
        .eq('status', 'active')
        .lt('oc_balance', LOW_OC_THRESHOLD)
        .order('oc_balance', { ascending: true })
        .limit(5)
    ),
    executeQuery<DataResponse<OcTransactionRow[]>>(
      client
        .from('oc_transactions')
        .select('id, store_id, franchise_id, transaction_type, amount, amount_oc, amount_krw, balance_after, description, created_at')
        .order('created_at', { ascending: false })
        .limit(5)
    ),
    executeQuery<DataResponse<AuditLogRow[]>>(
      client
        .from('audit_logs')
        .select('id, created_at, admin_id, action_type, target_type, target_id, store_id')
        .order('created_at', { ascending: false })
        .limit(5)
    ),
  ]);

  [
    activeFranchises,
    stores,
    users,
    franchiseBalances,
    dailyActiveUsers,
    dailyTransactions,
    lowBalanceFranchises,
    recentOcTransactions,
    recentAuditLogs,
  ].forEach((response) => assertNoError(response, '시스템 관리자 대시보드 데이터를 불러오지 못했습니다.'));

  const totalOcCirculation = rowsOrEmpty(franchiseBalances).reduce(
    (sum, franchise) => sum + (franchise.oc_balance ?? 0),
    0
  );
  const dailyRevenue = rowsOrEmpty(dailyTransactions).reduce((sum, transaction) => {
    const amount = typeof transaction.amount_krw === 'string' ? Number(transaction.amount_krw) : transaction.amount_krw;
    return sum + (Number.isFinite(amount) ? Number(amount) : 0);
  }, 0);

  return {
    metrics: [
      { label: '활성 프랜차이즈', value: formatInteger(countOrZero(activeFranchises)), note: 'status = active' },
      { label: '매장', value: formatInteger(countOrZero(stores)), note: '전체 매장' },
      { label: '사용자', value: formatInteger(countOrZero(users)), note: 'platform_admin 제외' },
      { label: '최근 활성 사용자', value: formatInteger(countOrZero(dailyActiveUsers)), note: '최근 24시간' },
      { label: '프랜차이즈 OC', value: formatOc(totalOcCirculation), note: 'franchises.oc_balance 합계' },
      { label: '최근 OC 거래', value: formatInteger(dailyTransactions.count ?? 0), note: `${formatCurrency(dailyRevenue)} / 24시간` },
    ],
    lowBalanceFranchises: rowsOrEmpty(lowBalanceFranchises),
    recentOcTransactions: rowsOrEmpty(recentOcTransactions),
    recentAuditLogs: rowsOrEmpty(recentAuditLogs),
  };
}

export async function fetchFranchisePage(
  client: LegacyDataClient,
  filters: FranchiseFilters,
  cursor: string | null,
  limit = SYSTEM_ADMIN_PAGE_SIZE
): Promise<PaginatedResult<FranchiseRow>> {
  let query = client
    .from('franchises')
    .select('id, name, status, oc_balance, region, commission_rate, exchange_rate, created_at, updated_at');

  if (filters.status) {
    query = query.eq('status', filters.status);
  }

  if (filters.search?.trim()) {
    query = query.ilike('name', `%${filters.search.trim()}%`);
  }

  if (cursor) {
    query = query.lt('created_at', decodeCursor(cursor).createdAt);
  }

  const response = (await query.order('created_at', { ascending: false }).limit(limit + 1)) as DataResponse<FranchiseRow[]>;
  assertNoError(response, '프랜차이즈 목록을 불러오지 못했습니다.');

  return buildPage(rowsOrEmpty(response), limit);
}

export async function fetchStorePage(
  client: LegacyDataClient,
  filters: StoreFilters,
  cursor: string | null,
  limit = SYSTEM_ADMIN_PAGE_SIZE
): Promise<PaginatedResult<StoreRow>> {
  let query = client
    .from('stores')
    .select('id, name, oc_balance, krw_per_1000_points, franchise_id, created_at, updated_at, franchise:franchises(id, name)');

  if (filters.lowOcOnly) {
    query = query.lt('oc_balance', 10);
  }

  if (filters.search?.trim()) {
    query = query.ilike('name', `%${filters.search.trim()}%`);
  }

  if (cursor) {
    query = query.lt('created_at', decodeCursor(cursor).createdAt);
  }

  const response = (await query.order('created_at', { ascending: false }).limit(limit + 1)) as DataResponse<StoreRow[]>;
  assertNoError(response, '매장 목록을 불러오지 못했습니다.');

  return buildPage(rowsOrEmpty(response), limit);
}

export async function fetchUserPage(
  client: LegacyDataClient,
  filters: UserFilters,
  cursor: string | null,
  limit = SYSTEM_ADMIN_PAGE_SIZE
): Promise<PaginatedResult<UserRow>> {
  let query = client
    .from('users')
    .select(USER_SELECT);

  if (filters.role) {
    query = query.eq('role', filters.role);
  }

  if (filters.status) {
    query = query.eq('status', filters.status);
  }

  if (filters.search?.trim()) {
    const search = filters.search.trim();
    query = query.or(
      `username.ilike.%${search}%,internal_login_id.ilike.%${search}%,nickname.ilike.%${search}%,phone_number.ilike.%${search}%,staff_phone_number.ilike.%${search}%`
    );
  }

  if (cursor) {
    query = query.lt('created_at', decodeCursor(cursor).createdAt);
  }

  const response = (await query.order('created_at', { ascending: false }).limit(limit + 1)) as DataResponse<UserRow[]>;
  assertNoError(response, '사용자 목록을 불러오지 못했습니다.');

  return buildPage(rowsOrEmpty(response), limit);
}

export async function fetchOcTransactionPage(
  client: LegacyDataClient,
  filters: OcFilters,
  cursor: string | null,
  limit = SYSTEM_ADMIN_PAGE_SIZE
): Promise<PaginatedResult<OcTransactionRow>> {
  let query = client
    .from('oc_transactions')
    .select('id, store_id, franchise_id, transaction_type, amount, amount_oc, amount_krw, balance_after, description, created_at');

  if (filters.transactionType) {
    query = query.eq('transaction_type', filters.transactionType);
  }

  if (cursor) {
    query = query.lt('created_at', decodeCursor(cursor).createdAt);
  }

  const response = (await query.order('created_at', { ascending: false }).limit(limit + 1)) as DataResponse<OcTransactionRow[]>;
  assertNoError(response, 'OC 거래 목록을 불러오지 못했습니다.');

  return buildPage(rowsOrEmpty(response), limit);
}

export async function fetchAuditLogPage(
  client: LegacyDataClient,
  filters: AuditFilters,
  cursor: string | null,
  limit = SYSTEM_ADMIN_PAGE_SIZE
): Promise<PaginatedResult<AuditLogRow>> {
  let query = client.from('audit_logs').select('id, created_at, admin_id, action_type, target_type, target_id, store_id');

  if (filters.targetType?.trim()) {
    query = query.eq('target_type', filters.targetType.trim());
  }

  if (filters.search?.trim()) {
    const search = filters.search.trim();

    if (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(search)) {
      query = query.or(`id.eq.${search},admin_id.eq.${search},target_id.eq.${search},store_id.eq.${search}`);
    } else {
      query = query.or(`action_type.ilike.%${search}%,target_type.ilike.%${search}%`);
    }
  }

  if (cursor) {
    const decoded = decodeCursor(cursor);
    query = query.or(`created_at.lt.${decoded.createdAt},and(created_at.eq.${decoded.createdAt},id.lt.${decoded.id})`);
  }

  const response = (await query
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(limit + 1)) as DataResponse<AuditLogRow[]>;
  assertNoError(response, '감사 로그를 불러오지 못했습니다.');

  return buildPage(rowsOrEmpty(response), limit);
}

export async function fetchSettingPage(
  client: LegacyDataClient,
  filters: SettingFilters
): Promise<PaginatedResult<SystemSettingRow>> {
  let query = client
    .from('system_settings')
    .select('id, key, value, description, value_type, category, updated_at, updated_by');

  if (filters.category?.trim()) {
    query = query.eq('category', filters.category.trim());
  }

  const response = (await query.order('category', { ascending: true }).order('key', { ascending: true }).limit(100)) as DataResponse<SystemSettingRow[]>;
  assertNoError(response, '시스템 설정을 불러오지 못했습니다.');

  return {
    rows: rowsOrEmpty(response),
    hasMore: false,
    nextCursor: null,
  };
}

export async function createFranchise(client: LegacyDataClient, input: CreateFranchiseInput): Promise<FranchiseRow> {
  return callRpc<FranchiseRow>(
    client,
    'admin_create_franchise',
    {
      p_commission_rate: normalizeCommissionRate(input.commissionRate),
      p_exchange_rate: normalizePositiveNumber(input.exchangeRate, '환율'),
      p_name: normalizeRequiredText(input.name, '프랜차이즈명'),
      p_oc_balance: normalizeNonNegativeInteger(input.ocBalance, '초기 OC'),
      p_region: normalizeNullableText(input.region),
    },
    '프랜차이즈를 생성하지 못했습니다.',
    '생성된 프랜차이즈 정보를 확인하지 못했습니다.'
  );
}

export async function updateFranchise(client: LegacyDataClient, input: UpdateFranchiseInput): Promise<FranchiseRow> {
  return callRpc<FranchiseRow>(
    client,
    'admin_update_franchise',
    {
      p_commission_rate: normalizeCommissionRate(input.commissionRate),
      p_exchange_rate: normalizePositiveNumber(input.exchangeRate, '환율'),
      p_id: input.id,
      p_name: normalizeRequiredText(input.name, '프랜차이즈명'),
      p_region: normalizeNullableText(input.region),
    },
    '프랜차이즈를 수정하지 못했습니다.',
    '수정된 프랜차이즈 정보를 확인하지 못했습니다.'
  );
}

export async function setFranchiseStatus(client: LegacyDataClient, input: SetFranchiseStatusInput): Promise<FranchiseRow> {
  const isDeactivation = input.status === 'inactive';
  const confirmationPhrase = isDeactivation ? 'DEACTIVATE FRANCHISE' : 'ACTIVATE FRANCHISE';
  const reason = normalizeReason(input.reason);

  assertConfirmation(input.confirmationText, confirmationPhrase);

  return callRpc<FranchiseRow>(
    client,
    'admin_set_franchise_status',
    {
      p_id: input.id,
      p_reason: reason,
      p_status: input.status,
    },
    isDeactivation ? '프랜차이즈를 비활성화하지 못했습니다.' : '프랜차이즈를 활성화하지 못했습니다.',
    '상태가 변경된 프랜차이즈 정보를 확인하지 못했습니다.'
  );
}

export async function createStore(client: LegacyDataClient, input: CreateStoreInput): Promise<StoreRow> {
  return callRpc<StoreRow>(
    client,
    'admin_create_store',
    {
      p_exchange_rate: normalizePositiveNumber(input.krwPer1000Points, '포인트 환율'),
      p_franchise_id: input.franchiseId.trim() || null,
      p_name: normalizeRequiredText(input.name, '매장명', 2, 50),
      p_oc_balance: normalizeNonNegativeInteger(input.ocBalance, '초기 OC'),
    },
    '매장을 생성하지 못했습니다.',
    '생성된 매장 정보를 확인하지 못했습니다.'
  );
}

export async function updateStore(client: LegacyDataClient, input: UpdateStoreInput): Promise<StoreRow> {
  return callRpc<StoreRow>(
    client,
    'admin_update_store',
    {
      p_exchange_rate: normalizePositiveNumber(input.krwPer1000Points, '포인트 환율'),
      p_franchise_id: input.franchiseId.trim() || null,
      p_id: input.id,
      p_name: normalizeRequiredText(input.name, '매장명', 2, 50),
    },
    '매장을 수정하지 못했습니다.',
    '수정된 매장 정보를 확인하지 못했습니다.'
  );
}

export async function suspendUser(client: LegacyDataClient, input: SuspendUserInput): Promise<UserRow> {
  if (input.userId === input.currentAdminId) {
    throw new Error('본인 계정은 정지할 수 없습니다.');
  }

  const reason = normalizeReason(input.reason);
  const duration = input.duration.trim() || 'indefinite';

  return callRpc<UserRow>(
    client,
    'admin_set_user_status',
    {
      p_duration: duration,
      p_reason: reason,
      p_status: 'suspended',
      p_user_id: input.userId,
    },
    '사용자를 정지하지 못했습니다.',
    '정지된 사용자 정보를 확인하지 못했습니다.'
  );
}

export async function unsuspendUser(client: LegacyDataClient, input: UnsuspendUserInput): Promise<UserRow> {
  if (input.userId === input.currentAdminId) {
    throw new Error('본인 계정 상태는 여기서 변경할 수 없습니다.');
  }

  const reason = normalizeReason(input.reason);

  return callRpc<UserRow>(
    client,
    'admin_set_user_status',
    {
      p_duration: null,
      p_reason: reason,
      p_status: 'active',
      p_user_id: input.userId,
    },
    '사용자 정지를 해제하지 못했습니다.',
    '정지 해제된 사용자 정보를 확인하지 못했습니다.'
  );
}

export async function resetUserPassword(client: LegacyDataClient, input: ResetUserPasswordInput): Promise<void> {
  const validationError = validateUserPasswordResetInput(input);

  if (validationError) {
    throw new Error(validationError);
  }

  const reason = normalizeReason(input.reason);
  const response = await client.functions.invoke('admin-reset-password', {
    body: {
      targetUserId: input.userId,
      newPassword: input.newPassword,
      reason,
      source: input.source ?? 'system_admin_react',
    },
  }) as { data: ResetPasswordFunctionResponse | null; error: unknown };

  if (response.error) {
    throw new Error((await readFunctionErrorMessage(response.error)) ?? '비밀번호를 재설정하지 못했습니다.');
  }

  if (response.data?.error) {
    throw new Error(response.data.error);
  }

  if (!response.data?.ok) {
    throw new Error('비밀번호 재설정 결과를 확인하지 못했습니다.');
  }
}

export async function createAdminUser(client: LegacyDataClient, input: CreateAdminUserInput): Promise<UserRow> {
  const validationError = validateCreateAdminUserInput(input);

  if (validationError) {
    throw new Error(validationError);
  }

  const response = await client.functions.invoke('admin-create-user', {
    body: {
      role: input.role,
      username: input.username.trim(),
      password: input.password,
      nickname: input.nickname.trim(),
      staffPhoneNumber: input.staffPhoneNumber.trim() || null,
      franchiseId: input.role === 'super_admin' ? input.franchiseId : null,
      storeId: input.role === 'staff_admin' ? input.storeId : null,
      reason: normalizeReason(input.reason),
      source: input.source ?? 'system_admin_react',
    },
  }) as { data: CreateAdminUserFunctionResponse | null; error: unknown };

  if (response.error) {
    throw new Error((await readFunctionErrorMessage(response.error)) ?? '관리자 계정을 생성하지 못했습니다.');
  }

  if (response.data?.error) {
    throw new Error(response.data.error);
  }

  if (!response.data?.ok || !response.data.user) {
    throw new Error('생성된 관리자 계정을 확인하지 못했습니다.');
  }

  return response.data.user;
}

export async function issueOcCredit(client: LegacyDataClient, input: IssueOcCreditInput): Promise<OcMutationResult> {
  const amount = normalizeInteger(input.amount, 'OC');
  const reason = normalizeReason(input.reason);

  if (amount <= 0) {
    throw new Error('발행 OC는 0보다 커야 합니다.');
  }

  if (!Number.isFinite(input.amountKrw) || input.amountKrw <= 0) {
    throw new Error('결제 금액은 0보다 커야 합니다.');
  }

  const response = (await client.rpc('issue_oc_credit', {
    p_amount: amount,
    p_amount_krw: input.amountKrw,
    p_franchise_id: input.franchiseId,
    p_reason: reason,
  })) as DataResponse<OcMutationResult>;

  assertNoError(response, 'OC를 발행하지 못했습니다.');

  if (!response.data) {
    throw new Error('OC 발행 결과를 확인하지 못했습니다.');
  }

  return response.data;
}

export async function adjustOcCredit(client: LegacyDataClient, input: AdjustOcCreditInput): Promise<OcMutationResult> {
  const amount = normalizeInteger(input.amount, '조정 OC');
  const reason = normalizeReason(input.reason);

  if (amount === 0) {
    throw new Error('조정 OC는 0일 수 없습니다.');
  }

  const response = (await client.rpc('adjust_oc_credit', {
    p_amount: amount,
    p_franchise_id: input.franchiseId,
    p_reason: reason,
  })) as DataResponse<OcMutationResult>;

  assertNoError(response, 'OC를 조정하지 못했습니다.');

  if (!response.data) {
    throw new Error('OC 조정 결과를 확인하지 못했습니다.');
  }

  return response.data;
}

export async function updateSystemSetting(
  client: LegacyDataClient,
  input: UpdateSystemSettingInput
): Promise<SystemSettingRow> {
  const phrase = `UPDATE ${input.key}`;
  assertConfirmation(input.confirmationText, phrase);

  const reason = normalizeReason(input.reason);
  const nextValue = parseSystemSettingValue(input.valueText, input.valueType);

  return callRpc<SystemSettingRow>(
    client,
    'admin_update_system_setting',
    {
      p_confirmation_text: input.confirmationText.trim(),
      p_id: input.id,
      p_reason: reason,
      p_value: nextValue,
    },
    '시스템 설정을 업데이트하지 못했습니다.',
    '업데이트된 시스템 설정을 확인하지 못했습니다.'
  );
}
